/* Stage 73.18 — State-wise PT + LWF Configuration UI.
 *
 * Shows the admin one card per state with:
 *   - Employee count under that state
 *   - PT slabs (editable inline)
 *   - LWF amounts (employee + employer) + deduction months
 *   - "Configured by tenant" badge vs falling back to bundled defaults
 *
 * Top of page: "Seed standard rates for all states" button (idempotent).
 *
 * Mounted at /statutory/state-rates by App.js.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, MapPin, Plus, Trash2, RefreshCw, Save, X, Info, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/* Stage 73.18 — defensive JSON parser. Some browser extensions / proxies
 * silently consume the fetch response body before user code reads it, which
 * surfaces as "Failed to execute 'json' on 'Response': body stream already
 * read". Reading as text first then parsing avoids the race and gives us
 * a useful fallback error when the body really isn't JSON.
 */
async function readJson(r) {
  let text = '';
  try {
    text = await r.text();
  } catch (e) {
    throw new Error(`Failed to read response body (${r.status}): ${e.message}`);
  }
  if (!text) return null;
  try { return JSON.parse(text); }
  catch { throw new Error(`Server returned non-JSON (${r.status}): ${text.slice(0, 200)}`); }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function StatutoryStateRates() {
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editingPt, setEditingPt] = useState(null);  // state name being edited
  const [editingLwf, setEditingLwf] = useState(null);
  const [editingSlabs, setEditingSlabs] = useState([]);
  const [editingLwfCfg, setEditingLwfCfg] = useState({});
  // Stage 73.20 — PT policy editor (deduction policy + slab months per state).
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editingPolicyCfg, setEditingPolicyCfg] = useState({});
  // Stage 73.20e — PF config status + one-click wage_floor repair.
  // Initialize to 'loading' so the banner section shows activity even
  // before the first fetch completes (helps users spot deploy drift).
  const [pfStatus, setPfStatus] = useState({ _loading: true });
  const [pfRepairing, setPfRepairing] = useState(false);
  // Stage 73.20f — bulk state assignment for unassigned employees.
  const [bulkStateValue, setBulkStateValue] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Load company list
  useEffect(() => {
    fetch(`${API_URL}/companies`, { credentials: 'include' })
      .then(r => r.text())
      .then(t => { try { return JSON.parse(t); } catch { return []; } })
      .then(d => {
        const list = Array.isArray(d) ? d : (d.companies || []);
        setCompanies(list);
        setCompanyId((cur) => (list.length && !cur ? (list[0]._id || list[0].id) : cur));
      })
      .catch(() => {});
  }, []);

  // Load state config when company changes
  const loadPfStatus = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/pf-config-status?company_id=${companyId}`, { credentials: 'include' });
      if (r.status === 404) {
        setPfStatus({
          _error: 'endpoint_missing',
          _http_status: 404,
          _message: 'Backend has not been redeployed with the Stage 73.20e PF diagnostic endpoint yet. Click Deploy again — the new /api/payroll/statutory/pf-config-status endpoint is required.',
        });
        return;
      }
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        setPfStatus({
          _error: 'http_error',
          _http_status: r.status,
          _message: `HTTP ${r.status} from /pf-config-status. ${txt.slice(0, 200)}`,
        });
        return;
      }
      setPfStatus(await readJson(r));
    } catch (e) {
      setPfStatus({
        _error: 'network_error',
        _message: `Could not reach /pf-config-status: ${e.message}`,
      });
    }
  }, [companyId]);
  const loadStateConfig = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/state-config?company_id=${companyId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await readJson(r));
    } catch (e) {
      toast.error('Failed to load state config: ' + e.message);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    loadStateConfig();
    loadPfStatus();
  }, [companyId, loadPfStatus, loadStateConfig]);

  // Stage 73.20e — PF config status. Surfaces stale `wage_floor` (e.g.
  // legacy ₹6,500 from pre-2014 EPF cap) and offers a one-click repair.
  //
  // ALWAYS sets pfStatus so the banner renders — when the new
  // /pf-config-status endpoint isn't deployed yet, we surface that
  // explicitly instead of silently hiding the banner.

  // Stage 73.20i — explicit PF policy switcher (no more one-way repair).
  const setPfPolicy = async (policyKey) => {
    if (!companyId) return;
    if (!window.confirm(
      `Switch PF policy to "${policyKey === 'uniform_1800' ? 'Uniform ₹1,800' : 'Strict EPFO'}" ` +
      `for this company? Rerun the active payroll period after switching to apply the new policy.`
    )) return;
    setPfRepairing(true);
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/pf-policy-set`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, policy: policyKey }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const body = await readJson(r);
      toast.success(`PF policy switched to ${body.policy_label}. ${body.next_step}`);
      await loadPfStatus();
    } catch (e) {
      toast.error('PF policy switch failed: ' + e.message);
    }
    setPfRepairing(false);
  };

  const repairPfFloor = async () => {
    if (!companyId) return;
    setPfRepairing(true);
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/pf-config-repair?company_id=${companyId}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await readJson(r);
      toast.success(
        `PF policy switched to Strict EPFO. ` +
        body.next_step
      );
      await loadPfStatus();
    } catch (e) {
      toast.error('PF repair failed: ' + e.message);
    }
    setPfRepairing(false);
  };

  // Stage 73.20f — apply a single state to ALL unassigned employees in
  // one click. Saves the user from re-uploading the entire employee CSV
  // just to set state="Tamil Nadu" for everyone.
  const applyBulkState = async () => {
    if (!companyId || !bulkStateValue) return;
    if (!window.confirm(
      `Assign state "${bulkStateValue}" to all ${data?.unassigned_employee_count || 0} ` +
      `unassigned employees in this company? Employees who already have a state will NOT be touched.`
    )) return;
    setBulkAssigning(true);
    try {
      const r = await fetch(`${API_URL}/employees/bulk-set-state`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: bulkStateValue,
          scope: 'all_unassigned',
          company_id: companyId,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const body = await readJson(r);
      toast.success(
        `${body.modified_count} employees set to state '${body.state_applied}'. ` +
        'Rerun the active payroll period — PT/LWF will pick the correct slab.'
      );
      setBulkStateValue('');
      await loadStateConfig();
    } catch (e) {
      toast.error('Bulk state assignment failed: ' + e.message);
    }
    setBulkAssigning(false);
  };


  const seedDefaults = async () => {
    if (!companyId) return;
    setSeeding(true);
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/state-config/seed?company_id=${companyId}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await readJson(r);
      toast.success(body?.message || 'Seeded successfully');
      await loadStateConfig();
    } catch (e) {
      toast.error('Seed failed: ' + e.message);
    }
    setSeeding(false);
  };

  // ---- PT slab editor
  const startPtEdit = (stateRow) => {
    setEditingPt(stateRow.state);
    setEditingSlabs(stateRow.pt.slabs.map(s => ({ ...s })));
  };
  const addSlab = () => {
    const last = editingSlabs[editingSlabs.length - 1];
    const from = last ? Number(last.to_amount) + 1 : 0;
    setEditingSlabs([...editingSlabs, { from_amount: from, to_amount: from + 10000, tax_amount: 0 }]);
  };
  const removeSlab = (i) => setEditingSlabs(editingSlabs.filter((_, idx) => idx !== i));
  const updateSlab = (i, key, val) => {
    const next = [...editingSlabs];
    next[i] = { ...next[i], [key]: val === '' ? '' : Number(val) };
    setEditingSlabs(next);
  };
  const savePtSlabs = async () => {
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/state-config/pt/${encodeURIComponent(editingPt)}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, slabs: editingSlabs }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await readJson(r);
      toast.success(body?.message || 'PT slabs saved');
      setEditingPt(null);
      await loadStateConfig();
    } catch (e) { toast.error('Save failed: ' + e.message); }
  };

  // ---- Stage 73.20 — PT policy editor (deduction_policy + slab_months)
  const startPolicyEdit = (stateRow) => {
    setEditingPolicy(stateRow.state);
    setEditingPolicyCfg({
      deduction_policy: stateRow.pt.policy?.deduction_policy || 'monthly_spread',
      slab_months: stateRow.pt.policy?.slab_months || Array.from({ length: 12 }, (_, i) => i + 1),
      slab_basis: stateRow.pt.policy?.slab_basis || 'monthly_gross',
    });
  };
  const toggleSlabMonth = (m) => {
    const s = new Set(editingPolicyCfg.slab_months || []);
    if (s.has(m)) s.delete(m); else s.add(m);
    setEditingPolicyCfg({ ...editingPolicyCfg, slab_months: Array.from(s).sort((a, b) => a - b) });
  };
  const savePtPolicy = async () => {
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/state-config/pt-policy/${encodeURIComponent(editingPolicy)}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, ...editingPolicyCfg }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await readJson(r);
      toast.success(body?.message || 'PT policy saved');
      setEditingPolicy(null);
      await loadStateConfig();
    } catch (e) { toast.error('Save failed: ' + e.message); }
  };

  // ---- LWF editor
  const startLwfEdit = (stateRow) => {
    setEditingLwf(stateRow.state);
    setEditingLwfCfg({ ...stateRow.lwf.config });
  };
  const toggleLwfMonth = (m) => {
    const months = new Set(editingLwfCfg.deduction_months || []);
    if (months.has(m)) months.delete(m); else months.add(m);
    setEditingLwfCfg({ ...editingLwfCfg, deduction_months: Array.from(months).sort((a, b) => a - b) });
  };
  const saveLwfCfg = async () => {
    try {
      const r = await fetch(`${API_URL}/payroll/statutory/state-config/lwf/${encodeURIComponent(editingLwf)}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, ...editingLwfCfg }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await readJson(r);
      toast.success(body?.message || 'LWF saved');
      setEditingLwf(null);
      await loadStateConfig();
    } catch (e) { toast.error('Save failed: ' + e.message); }
  };

  const states = useMemo(() => data?.states || [], [data]);
  const totalEmps = useMemo(() => states.reduce((s, x) => s + x.employee_count, 0), [states]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">State-wise Statutory Rates</h1>
          <p className="text-sm text-slate-500 mt-1">
            Professional Tax (PT) slabs and Labour Welfare Fund (LWF) contributions per state. Engine picks the right rate per employee based on their <code className="bg-slate-100 px-1 rounded">personal_details.state</code> at payroll-run time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="company-select">
            {companies.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>)}
          </select>
          <button onClick={seedDefaults} disabled={seeding || loading} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium disabled:opacity-50" data-testid="seed-defaults-btn">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Seed Defaults
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="info-banner">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p><b>How it works:</b> An employee's <b>personal_details.state</b> determines their PT slab and LWF contribution. Salary register shows the deduction with the state name (e.g. "Professional Tax · Tamil Nadu"). Below: per-state config — green badge = your override; gray = bundled default.</p>
          {data?.unassigned_employee_count > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="unassigned-state-row">
              <p className="font-medium text-amber-800 flex-1 min-w-[260px]">
                ⚠ {data.unassigned_employee_count} employees have NO state set — they'll use the company default fallback. Assign now:
              </p>
              <select
                value={bulkStateValue}
                onChange={e => setBulkStateValue(e.target.value)}
                className="border border-amber-300 rounded px-2 py-1 text-xs bg-white"
                data-testid="bulk-state-select"
              >
                <option value="">— pick state —</option>
                {(states || []).map(s => (
                  <option key={s.state} value={s.state}>{s.state}</option>
                ))}
              </select>
              <button
                onClick={applyBulkState}
                disabled={!bulkStateValue || bulkAssigning}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium disabled:opacity-50"
                data-testid="bulk-set-state-btn"
              >
                {bulkAssigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Apply to all {data.unassigned_employee_count} unassigned
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stage 73.20e — PF wage_floor health banner. Reads /pf-config-status
          and surfaces a one-click repair when wage_floor is stuck on a
          legacy value (e.g. pre-2014 EPF cap ₹6,500 → engine returns
          ₹1,740 instead of the standard ₹1,800).

          Stage 73.20e (banner-v2): ALWAYS renders so production deploy
          drift is visible. If the new endpoint 404s, we explicitly show
          a redeploy prompt instead of silently hiding. */}
      {pfStatus && pfStatus._loading && (
        <div
          className="rounded-md p-3 text-xs border bg-slate-50 border-slate-200 text-slate-600 flex items-center gap-2"
          data-testid="pf-status-banner"
        >
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          <span>Checking PF wage_floor configuration…</span>
        </div>
      )}
      {pfStatus && pfStatus._error && (
        <div
          className="rounded-md p-3 text-xs border flex items-start gap-2 bg-amber-50 border-amber-300 text-amber-900"
          data-testid="pf-status-banner"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800" data-testid="pf-status-deploy-needed">
              ⚠ PF diagnostic endpoint not yet deployed (HTTP {pfStatus._http_status || 'network'})
            </p>
            <p className="mt-0.5">{pfStatus._message}</p>
            <p className="mt-1 text-amber-700">
              Redeploy both frontend and backend. The Stage 73.20e fix needs both frontend AND backend redeployed.
            </p>
          </div>
        </div>
      )}
      {pfStatus && !pfStatus._error && !pfStatus._loading && pfStatus.configured === false && (
        <div
          className="rounded-md p-3 text-xs border bg-amber-50 border-amber-300 text-amber-900 flex items-start gap-2"
          data-testid="pf-status-banner"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800" data-testid="pf-status-not-configured">
              ⚠ No PF config for this company
            </p>
            <p className="mt-0.5">
              Statutory PF config has not been created yet. Go to <b>Statutory → Setup Master</b> to configure PF (employee/employer rate, wage_floor=₹15,000, wage_ceiling=₹15,000) before processing payroll.
            </p>
          </div>
        </div>
      )}
      {pfStatus && !pfStatus._error && !pfStatus._loading && pfStatus.configured !== false && (
        <div
          className={`rounded-md p-3 text-xs border flex items-start gap-2 ${pfStatus.needs_repair ? 'bg-rose-50 border-rose-300 text-rose-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
          data-testid="pf-status-banner"
        >
          <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pfStatus.needs_repair ? 'text-rose-600' : 'text-emerald-600'}`} />
          <div className="flex-1">
            {pfStatus.needs_repair ? (
              <>
                <p className="font-semibold text-rose-800" data-testid="pf-status-needs-repair">
                  ⚠ PF policy is set to {pfStatus.policy_label || 'a non-EPFO mode'} — switch to Strict EPFO
                </p>
                <p className="mt-0.5">{pfStatus.diagnosis}</p>
                {pfStatus.scenarios && pfStatus.scenarios.length > 0 && (
                  <div className="mt-1.5 text-rose-800/90">
                    Current engine output:
                    <ul className="ml-4 list-disc">
                      {pfStatus.scenarios.map((s, i) => (
                        <li key={i}>
                          {s.label}: <b>₹{s.engine_pf}</b>
                          {' '}<span className="text-rose-700/70">(strict EPFO expects ₹{s.expected_strict_epfo})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={repairPfFloor}
                  disabled={pfRepairing}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-xs font-medium disabled:opacity-50"
                  data-testid="pf-repair-btn"
                >
                  {pfRepairing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Switch to Strict EPFO &amp; Repair
                </button>
              </>
            ) : (
              <>
                <p className="font-semibold text-emerald-800" data-testid="pf-status-healthy">
                  ✓ Active PF Policy: Earned Gross − Earned HRA, capped at ₹15,000
                </p>
                <p className="mt-0.5 text-emerald-800/90">
                  <b>PF Wage</b> = Earned Gross (excluding Monthly Inputs) − Earned HRA.
                  Then: PF Wage ≥ ₹15,000 → ₹1,800; PF Wage &lt; ₹15,000 → 12% × PF Wage.
                  <span className="block mt-0.5 text-emerald-800/80">
                    LOP impact is automatic (Earned figures already account for payable days).
                    ESI continues on <b>Fixed Gross</b> (unchanged).
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0052FF] mx-auto" /></div>
      ) : (
        <div className="space-y-4" data-testid="state-rows">
          <div className="text-xs text-slate-500 flex items-center gap-4 mb-2 px-1">
            <span>📍 {states.length} states</span>
            <span>👥 {totalEmps} employees assigned</span>
            <span>🪪 {data?.unassigned_employee_count || 0} unassigned</span>
          </div>

          {states.map(s => (
            <div key={s.state} className={`bg-white rounded-lg border ${s.employee_count > 0 ? 'border-[#0052FF]/30 shadow-sm' : 'border-slate-200'} overflow-hidden`} data-testid={`state-row-${s.state}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <h2 className="font-semibold text-slate-800">{s.state}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.employee_count > 0 ? 'bg-blue-100 text-[#0052FF]' : 'bg-slate-100 text-slate-500'}`}>
                    {s.employee_count} {s.employee_count === 1 ? 'employee' : 'employees'}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4">
                {/* PT slabs */}
                <div className="border border-slate-200 rounded-lg p-3" data-testid={`pt-card-${s.state}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">Professional Tax (PT)</h3>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.pt.configured_by_tenant ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {s.pt.configured_by_tenant ? 'Tenant override' : 'Default'}
                      </span>
                    </div>
                    {editingPt !== s.state && (
                      <button onClick={() => startPtEdit(s)} className="text-xs flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700" data-testid={`edit-pt-${s.state}`}>
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                    )}
                  </div>
                  {editingPt === s.state ? (
                    <div className="space-y-1.5">
                      <div className="text-[10px] text-slate-500 grid grid-cols-7 gap-1 font-medium">
                        <span className="col-span-2">From (₹/mo)</span><span className="col-span-2">To (₹/mo)</span><span className="col-span-2">Tax (₹/mo)</span><span></span>
                      </div>
                      {editingSlabs.map((slab, i) => (
                        <div key={i} className="grid grid-cols-7 gap-1 text-xs">
                          <input type="number" value={slab.from_amount} onChange={e => updateSlab(i, 'from_amount', e.target.value)} className="col-span-2 border border-slate-200 rounded px-1.5 py-1 font-mono" />
                          <input type="number" value={slab.to_amount} onChange={e => updateSlab(i, 'to_amount', e.target.value)} className="col-span-2 border border-slate-200 rounded px-1.5 py-1 font-mono" />
                          <input type="number" value={slab.tax_amount} onChange={e => updateSlab(i, 'tax_amount', e.target.value)} className="col-span-2 border border-slate-200 rounded px-1.5 py-1 font-mono" />
                          <button onClick={() => removeSlab(i)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2">
                        <button onClick={addSlab} className="text-xs flex items-center gap-1 text-[#0052FF] hover:underline">
                          <Plus className="w-3 h-3" /> Add slab
                        </button>
                        <div className="flex gap-1">
                          <button onClick={() => setEditingPt(null)} className="text-xs flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700"><X className="w-3 h-3" />Cancel</button>
                          <button onClick={savePtSlabs} className="text-xs flex items-center gap-1 px-2 py-1 bg-[#0052FF] hover:bg-[#0040cc] text-white rounded" data-testid={`save-pt-${s.state}`}><Save className="w-3 h-3" />Save</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-700">
                      {s.pt.slabs.length === 0 ? (
                        <span className="text-slate-400 italic">No slabs configured</span>
                      ) : (
                        <table className="w-full">
                          <tbody>
                            {s.pt.slabs.map((slab, i) => (
                              <tr key={i} className="border-b border-slate-100 last:border-0">
                                <td className="py-0.5 font-mono">₹{Number(slab.from_amount).toLocaleString('en-IN')}</td>
                                <td className="py-0.5 text-slate-400">–</td>
                                <td className="py-0.5 font-mono">{slab.to_amount > 99999999 ? 'and above' : `₹${Number(slab.to_amount).toLocaleString('en-IN')}`}</td>
                                <td className="py-0.5 text-right font-mono font-semibold text-rose-700">₹{Number(slab.tax_amount).toLocaleString('en-IN')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Stage 73.20 — PT deduction policy editor */}
                  <div className="mt-3 pt-2.5 border-t border-slate-100" data-testid={`pt-policy-${s.state}`}>
                    {editingPolicy === s.state ? (
                      <div className="space-y-2">
                        <div className="text-[11px] font-semibold text-slate-700">Slab basis (which gross drives the slab lookup)</div>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          {[
                            { v: 'monthly_gross', l: 'Monthly gross' },
                            { v: 'halfyearly_gross', l: 'Half-year cumulative' },
                            { v: 'annual_gross', l: 'Annual cumulative' },
                            { v: 'half_year_slab_spread', l: 'Half-Year Slab Spread' },
                          ].map(opt => (
                            <label key={opt.v}
                              title={opt.v === 'half_year_slab_spread' ? 'Calculates Professional Tax using Half-Year cumulative slab and spreads the slab amount equally across each month in the cycle.' : undefined}
                              className={`flex items-center gap-1 cursor-pointer px-2 py-1 rounded border ${
                              editingPolicyCfg.slab_basis === opt.v
                                ? 'bg-blue-100 border-blue-400 text-blue-900'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                              <input type="radio" name={`basis-${s.state}`} value={opt.v}
                                checked={editingPolicyCfg.slab_basis === opt.v}
                                onChange={() => {
                                  // Stage 73.20c — when basis changes, auto-suggest slab_months
                                  // to match the statute (TN→[Aug,Feb], AP→[Sep,Feb], KA→[Mar], etc.).
                                  // Admin can still override afterwards.
                                  const stateMonthDefaults = {
                                    'Tamil Nadu':     [8, 2],
                                    'Andhra Pradesh': [9, 2],
                                    'Kerala':         [9, 3],
                                    'Karnataka':      [3],
                                    'Telangana':      [2],
                                  };
                                  let nextMonths = editingPolicyCfg.slab_months;
                                  if (opt.v === 'halfyearly_gross') {
                                    nextMonths = stateMonthDefaults[s.state] || [9, 3];
                                  } else if (opt.v === 'annual_gross') {
                                    nextMonths = stateMonthDefaults[s.state] || [3];
                                  } else if (opt.v === 'monthly_gross') {
                                    nextMonths = Array.from({ length: 12 }, (_, i) => i + 1);
                                  } else if (opt.v === 'half_year_slab_spread') {
                                    nextMonths = Array.from({ length: 12 }, (_, i) => i + 1);
                                  }
                                  setEditingPolicyCfg({ ...editingPolicyCfg, slab_basis: opt.v, slab_months: nextMonths });
                                }}
                                data-testid={`basis-${opt.v}-${s.state}`}
                                className="w-3 h-3" />
                              <span>{opt.l}</span>
                            </label>
                          ))}
                        </div>
                        {editingPolicyCfg.slab_basis === 'half_year_slab_spread' && (
                          <div className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 leading-relaxed" title="Calculates Professional Tax using Half-Year cumulative slab and spreads the slab amount equally across each month in the cycle.">
                            <b>Half-Year Slab Spread policy:</b> Calculates Professional Tax using Half-Year cumulative slab and spreads the slab amount equally across each month in the cycle. Final month adjusts any remaining paise.
                          </div>
                        )}
                        {editingPolicyCfg.slab_basis === 'halfyearly_gross' && (
                          <div className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1.5 leading-relaxed">
                            <b>Half-year basis math (locked):</b> Annual due = slab × <b>2 cycles</b>. Monthly spread = slab ÷ <b>6 months</b>.<br />
                            <b>Slab months</b> below are just the months in which the statute mandates the actual deduction/return (e.g. TN: Aug + Feb). <em>They no longer affect the cycle count</em> — the basis radio above does.
                          </div>
                        )}
                        {editingPolicyCfg.slab_basis === 'annual_gross' && (
                          <div className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5 leading-relaxed">
                            <b>Annual basis math (locked):</b> Annual due = slab × <b>1 cycle</b>. Monthly spread = slab ÷ <b>12 months</b>.
                          </div>
                        )}
                        <div className="text-[11px] font-semibold text-slate-700">Deduction Policy</div>
                        <div className="flex gap-3 text-[11px]">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="radio" name={`policy-${s.state}`} value="monthly_spread"
                              checked={editingPolicyCfg.deduction_policy === 'monthly_spread'}
                              onChange={() => setEditingPolicyCfg({ ...editingPolicyCfg, deduction_policy: 'monthly_spread' })}
                              data-testid={`policy-spread-${s.state}`} />
                            <span>Monthly spread (1/12 each month, no mismatch)</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="radio" name={`policy-${s.state}`} value="lump_sum_at_slab_month"
                              checked={editingPolicyCfg.deduction_policy === 'lump_sum_at_slab_month'}
                              onChange={() => setEditingPolicyCfg({ ...editingPolicyCfg, deduction_policy: 'lump_sum_at_slab_month' })}
                              data-testid={`policy-lump-${s.state}`} />
                            <span>Lump-sum at slab month(s)</span>
                          </label>
                        </div>
                        <div className="text-[11px] font-semibold text-slate-700 mt-1">Slab months (when statute mandates deduction)</div>
                        <div className="grid grid-cols-12 gap-1">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                            const active = (editingPolicyCfg.slab_months || []).includes(m);
                            return (
                              <button key={m} type="button"
                                onClick={() => toggleSlabMonth(m)}
                                className={`text-[10px] py-1 rounded border ${active
                                  ? 'bg-[#0052FF] text-white border-[#0052FF]'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex justify-end gap-1 pt-1">
                          <button onClick={() => setEditingPolicy(null)}
                            className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700">
                            <X className="w-3 h-3 inline mr-0.5" />Cancel
                          </button>
                          <button onClick={savePtPolicy}
                            className="text-[11px] px-2 py-1 bg-[#0052FF] hover:bg-[#0040cc] text-white rounded"
                            data-testid={`save-policy-${s.state}`}>
                            <Save className="w-3 h-3 inline mr-0.5" />Save Policy
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] text-slate-600">
                          <span className="font-semibold">Policy:</span>{' '}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            s.pt.policy?.deduction_policy === 'lump_sum_at_slab_month'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'}`}>
                            {s.pt.policy?.deduction_policy === 'lump_sum_at_slab_month' ? 'Lump-sum' : 'Monthly spread'}
                          </span>
                          {' · '}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            s.pt.policy?.slab_basis === 'half_year_slab_spread'
                              ? 'bg-emerald-100 text-emerald-800'
                              : s.pt.policy?.slab_basis === 'halfyearly_gross'
                                ? 'bg-purple-100 text-purple-800'
                                : s.pt.policy?.slab_basis === 'annual_gross'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-slate-100 text-slate-700'}`}
                                title={s.pt.policy?.slab_basis === 'half_year_slab_spread' ? 'Calculates Professional Tax using Half-Year cumulative slab and spreads the slab amount equally across each month in the cycle.' : undefined}>
                            Basis: {s.pt.policy?.slab_basis === 'half_year_slab_spread' ? 'Half-Year Slab Spread'
                              : s.pt.policy?.slab_basis === 'halfyearly_gross' ? '6-month cumulative'
                              : s.pt.policy?.slab_basis === 'annual_gross' ? 'Annual cumulative' : 'Monthly'}
                          </span>
                          {' · '}
                          <span className="text-slate-500">
                            Slab months: {(s.pt.policy?.slab_months || []).map(m =>
                              ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]).join(', ') || 'all 12'}
                          </span>
                          {s.pt.policy?.configured_by_tenant && (
                            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">override</span>
                          )}
                        </div>
                        <button onClick={() => startPolicyEdit(s)}
                          className="text-[11px] flex items-center gap-1 px-2 py-0.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-700"
                          data-testid={`edit-policy-${s.state}`}>
                          <Edit2 className="w-3 h-3" /> Policy
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* LWF */}
                <div className="border border-slate-200 rounded-lg p-3" data-testid={`lwf-card-${s.state}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">Labour Welfare Fund (LWF)</h3>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.lwf.configured_by_tenant ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {s.lwf.configured_by_tenant ? 'Tenant override' : 'Default'}
                      </span>
                    </div>
                    {editingLwf !== s.state && (
                      <button onClick={() => startLwfEdit(s)} className="text-xs flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700" data-testid={`edit-lwf-${s.state}`}>
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                    )}
                  </div>
                  {editingLwf === s.state ? (
                    <div className="space-y-2 text-xs">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-500 block mb-1">Employee Contribution (₹)</label>
                          <input type="number" step="0.01" value={editingLwfCfg.employee_amount} onChange={e => setEditingLwfCfg({ ...editingLwfCfg, employee_amount: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 font-mono" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block mb-1">Employer Contribution (₹)</label>
                          <input type="number" step="0.01" value={editingLwfCfg.employer_amount} onChange={e => setEditingLwfCfg({ ...editingLwfCfg, employer_amount: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 font-mono" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 block mb-1">Deduction Months (click to toggle)</label>
                        <div className="grid grid-cols-6 gap-1">
                          {MONTH_NAMES.map((m, idx) => {
                            const monthNum = idx + 1;
                            const active = (editingLwfCfg.deduction_months || []).includes(monthNum);
                            return (
                              <button key={m} onClick={() => toggleLwfMonth(monthNum)} className={`px-1.5 py-1 rounded text-[10px] font-medium ${active ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                {m}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex justify-end gap-1 pt-1">
                        <button onClick={() => setEditingLwf(null)} className="text-xs flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700"><X className="w-3 h-3" />Cancel</button>
                        <button onClick={saveLwfCfg} className="text-xs flex items-center gap-1 px-2 py-1 bg-[#0052FF] hover:bg-[#0040cc] text-white rounded" data-testid={`save-lwf-${s.state}`}><Save className="w-3 h-3" />Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-700 space-y-1">
                      <div className="flex justify-between">
                        <span>Employee</span>
                        <span className="font-mono font-semibold text-rose-700">₹{Number(s.lwf.config.employee_amount).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Employer</span>
                        <span className="font-mono font-semibold text-blue-700">₹{Number(s.lwf.config.employer_amount).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex flex-wrap gap-0.5 mt-1.5">
                        <span className="text-[10px] text-slate-500 mr-1">Months:</span>
                        {(s.lwf.config.deduction_months || []).length === 0 ? (
                          <span className="text-[10px] text-slate-400 italic">none</span>
                        ) : (
                          (s.lwf.config.deduction_months || []).map(m => (
                            <span key={m} className="text-[10px] bg-blue-50 text-[#0052FF] px-1 rounded">{MONTH_NAMES[m - 1]}</span>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
